/*
 * Popup campaign engine.
 *
 * <popup-campaigns> reads every <popup-campaign> child, decides which one is
 * eligible for the current visitor, and arms its triggers. Eligibility state
 * (impressions, dismissals, conversions) is persisted so that frequency caps
 * survive page loads.
 */

const POPUP_STORE_KEY = 'oscc:popups:v2';
const POPUP_SESSION_KEY = 'oscc:popups:session:v2';
const POPUP_DAY = 86400000;
const POPUP_SCROLL_GRACE = 3000;
const POPUP_SIGNUP_TIMEOUT = 15000;
const POPUP_FORCE_PARAM = 'popup';

/* Throwaway inbox providers. On a prize wheel these are the signature of
   "spin farming" — resubmitting until the top prize lands — which burns
   discount codes and drags down sending reputation.

   This is only the short head of the distribution, kept inline so the check
   works the instant a popup opens. The full community list is ~8,900 domains
   and 50KB gzipped, far too much to put on every page load, so it ships as a
   separate asset that is fetched once a campaign with blocking turned on
   actually opens. See popup-capture-app/sync-disposable-domains.mjs. */
const DISPOSABLE_CORE = [
  '10minutemail.com',
  'dispostable.com',
  'fakeinbox.com',
  'getairmail.com',
  'guerrillamail.com',
  'guerrillamail.info',
  'mail-temp.com',
  'mailinator.com',
  'maildrop.cc',
  'mintemail.com',
  'moakt.com',
  'mohmal.com',
  'sharklasers.com',
  'temp-mail.org',
  'tempmail.com',
  'tempmailo.com',
  'throwawaymail.com',
  'trashmail.com',
  'yopmail.com',
];

/* Accepts the hex Shopify colour settings emit, plus rgb()/rgba() in case a
   value is ever piped through a Liquid colour filter. */
function parseColor(value) {
  if (!value) return null;
  const hex = String(value).trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16));
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  }
  const parts = String(value).match(/-?\d+\.?\d*/g);
  return parts && parts.length >= 3 ? parts.slice(0, 3).map(Number) : null;
}

/* Picks black or white text for a wedge from its relative luminance, so a
   merchant can choose any segment colour without stranding the label. */
function readableInk(background) {
  const rgb = parseColor(background);
  if (!rgb) return '#ffffff';
  const [r, g, b] = rgb.map((channel) => {
    const ratio = channel / 255;
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.42 ? '#10182a' : '#ffffff';
}

/* Shrinks a wheel label to fit the radial space it has, then truncates only
   if shrinking is not enough. 0.55em per character approximates the average
   advance width of a bold sans-serif well enough for this. */
function fitLabel(label, span, startSize) {
  const widthOf = (text, size) => text.length * size * 0.55;
  let size = startSize;
  while (size > 6.5 && widthOf(label, size) > span) size -= 0.5;
  if (widthOf(label, size) <= span) return [label, size];

  const maxChars = Math.max(3, Math.floor(span / (size * 0.55)));
  return [`${label.slice(0, maxChars - 1).trimEnd()}…`, size];
}

/* Repeats the palette around the wheel, then repairs the seam. Alternating two
   colours over an odd number of wedges always lands the same colour either
   side of twelve o'clock, which a third colour resolves. */
function assignSliceColors(count, palette) {
  const colors = [];
  for (let index = 0; index < count; index += 1) colors.push(palette[index % palette.length]);
  if (count > 2 && colors[count - 1] === colors[0]) {
    const spare = palette.find((color) => color !== colors[0] && color !== colors[count - 2]);
    if (spare) colors[count - 1] = spare;
  }
  return colors;
}

function readStore(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

function writeStore(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    /* Private browsing or a full quota: frequency caps degrade to per-page. */
  }
}

class PopupState {
  constructor() {
    this.persistent = readStore(window.localStorage, POPUP_STORE_KEY, { campaigns: {} });
    if (!this.persistent.campaigns) this.persistent.campaigns = {};

    this.session = readStore(window.sessionStorage, POPUP_SESSION_KEY, { pageviews: 0, shows: 0, campaigns: {} });
    if (!this.session.campaigns) this.session.campaigns = {};
    this.session.pageviews = (this.session.pageviews || 0) + 1;
    this.saveSession();
  }

  savePersistent() {
    writeStore(window.localStorage, POPUP_STORE_KEY, this.persistent);
  }

  saveSession() {
    writeStore(window.sessionStorage, POPUP_SESSION_KEY, this.session);
  }

  campaign(id) {
    if (!this.persistent.campaigns[id]) this.persistent.campaigns[id] = {};
    return this.persistent.campaigns[id];
  }

  sessionCampaign(id) {
    if (!this.session.campaigns[id]) this.session.campaigns[id] = { shows: 0 };
    return this.session.campaigns[id];
  }

  get pageviews() {
    return this.session.pageviews;
  }

  get sessionShows() {
    return this.session.shows || 0;
  }

  get isReturningVisitor() {
    return Boolean(this.persistent.firstSeen) && Date.now() - this.persistent.firstSeen > POPUP_DAY;
  }

  get hasSubscribed() {
    return Boolean(this.persistent.subscribedAt);
  }

  markFirstSeen() {
    if (!this.persistent.firstSeen) {
      this.persistent.firstSeen = Date.now();
      this.savePersistent();
    }
  }

  recordShow(id) {
    const record = this.campaign(id);
    record.lastShown = Date.now();
    record.shows = (record.shows || 0) + 1;
    this.savePersistent();

    this.sessionCampaign(id).shows += 1;
    this.session.shows = this.sessionShows + 1;
    this.saveSession();
  }

  recordDismiss(id) {
    this.campaign(id).dismissedAt = Date.now();
    this.savePersistent();
  }

  recordConversion(id) {
    this.campaign(id).convertedAt = Date.now();
    this.persistent.subscribedAt = Date.now();
    this.savePersistent();
  }

  /* A stable 0-99 bucket, so percentage rollouts and A/B splits stay
     consistent for a visitor across sessions instead of re-rolling. */
  get bucket() {
    if (typeof this.persistent.bucket !== 'number') {
      this.persistent.bucket = Math.floor(Math.random() * 100);
      this.savePersistent();
    }
    return this.persistent.bucket;
  }
}

class PopupCampaign extends HTMLElement {
  constructor() {
    super();
    this.config = JSON.parse(this.querySelector('[data-popup-config]').textContent);
    this.dialog = this.querySelector('[data-popup-dialog]');
    this.overlay = this.querySelector('[data-popup-overlay]');
    this.teaser = this.querySelector('[data-popup-teaser]');
    this.forms = Array.from(this.querySelectorAll('[data-popup-form]'));
    this.timers = [];
    this.listeners = [];
    this.isOpen = false;
    this.step = 0;
    this.required = new Set();
    this.met = new Set();
  }

  connectedCallback() {
    this.querySelectorAll('[data-popup-close]').forEach((button) =>
      this.on(button, 'click', () => this.close('dismiss'))
    );
    this.querySelectorAll('[data-popup-advance]').forEach((button) =>
      this.on(button, 'click', () => this.goToStep(this.step + 1))
    );
    this.querySelectorAll('[data-popup-skip]').forEach((button) =>
      this.on(button, 'click', () => this.finish())
    );

    if (this.overlay) this.on(this.overlay, 'click', () => this.close('dismiss'));
    if (this.teaser) this.on(this.teaser, 'click', () => this.open('teaser'));
    this.forms.forEach((form) => this.on(form, 'submit', (event) => this.onSubmit(event)));

    const copyButton = this.querySelector('[data-popup-copy]');
    if (copyButton) this.on(copyButton, 'click', () => this.copyCode(copyButton));

    this.on(document, 'keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close('dismiss');
    });

    this.wheel = this.querySelector('[data-popup-wheel]');
    if (this.wheel) this.setupWheel();
  }

  disconnectedCallback() {
    this.disarm();
    this.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
    this.listeners = [];
  }

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push([target, type, handler]);
  }

  /* ---------------------------------------------------------------- triggers */

  arm() {
    const triggers = this.config.triggers || {};

    /* "all" is Klaviyo's compound recommendation (a short delay *and* a scroll
       threshold), which filters out visitors who never engaged. Exit intent,
       idle and add-to-cart stay independent so the popup can still fire for
       someone who is leaving before both conditions are met. */
    this.requireAll = triggers.logic === 'all';
    this.required = new Set();
    this.met = new Set();
    if (this.requireAll) {
      if (typeof triggers.delay === 'number') this.required.add('delay');
      if (typeof triggers.scroll === 'number') this.required.add('scroll');
    }

    if (typeof triggers.delay === 'number') {
      this.timers.push(window.setTimeout(() => this.fire('delay'), triggers.delay * 1000));
    }

    if (typeof triggers.scroll === 'number') {
      const evaluateScroll = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        const percent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
        if (percent >= triggers.scroll) this.fire('scroll');
      };
      this.on(window, 'scroll', evaluateScroll, { passive: true });
      /* A page shorter than the viewport never emits a scroll event, which
         would strand the trigger. Count it as read after a grace period
         instead of at load, so the popup is never the first thing seen. */
      this.timers.push(window.setTimeout(evaluateScroll, POPUP_SCROLL_GRACE));
    }

    if (typeof triggers.idle === 'number') this.armIdle(triggers.idle);
    if (triggers.exit) this.armExitIntent();
    if (triggers.addToCart) this.armAddToCart();
  }

  armIdle(seconds) {
    let timer;
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => this.fire('idle'), seconds * 1000);
      this.timers.push(timer);
    };
    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach((type) =>
      this.on(document, type, reset, { passive: true })
    );
    reset();
  }

  armExitIntent() {
    /* Desktop: cursor leaving through the top of the viewport toward the
       browser chrome. Mobile has no equivalent, so a fast upward flick near
       the top of the page stands in for "about to hit back". */
    this.on(document, 'mouseout', (event) => {
      if (event.relatedTarget || event.toElement) return;
      if (event.clientY > 0) return;
      this.fire('exit');
    });

    let lastY = window.scrollY;
    let lastTime = Date.now();
    this.on(
      window,
      'scroll',
      () => {
        const now = Date.now();
        const elapsed = now - lastTime;
        if (elapsed < 30) return;
        const velocity = (lastY - window.scrollY) / elapsed;
        lastY = window.scrollY;
        lastTime = now;
        if (velocity > 1.2 && window.scrollY < 400) this.fire('exit');
      },
      { passive: true }
    );
  }

  armAddToCart() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.fire('add_to_cart'));
  }

  disarm() {
    this.timers.forEach((timer) => window.clearTimeout(timer));
    this.timers = [];
    if (this.cartUnsubscribe) {
      this.cartUnsubscribe();
      this.cartUnsubscribe = null;
    }
  }

  fire(reason) {
    if (this.isOpen || this.hasFired) return;

    if (this.required.has(reason)) {
      this.met.add(reason);
      if (this.met.size < this.required.size) return;
    }

    if (!this.manager.claim(this)) return;
    this.hasFired = true;
    this.disarm();
    this.open(reason);
  }

  /* ------------------------------------------------------------ open / close */

  open(reason) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.openedBy = document.activeElement;
    this.manager.active = this;
    this.setAttribute('open', '');
    this.dataset.trigger = reason;
    this.hideTeaser();

    if (this.config.blockDisposable) this.manager.loadDisposableDomains();

    if (this.config.lockScroll) document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') {
      const focusTarget = this.dialog.querySelector('input, button, [tabindex]') || this.dialog;
      trapFocus(this.dialog, focusTarget);
    }

    if (!this.manager.previewing) this.manager.state.recordShow(this.config.id);
    this.emit('shown', { trigger: reason });
  }

  close(outcome) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);

    if (outcome === 'dismiss') {
      if (!this.manager.previewing) this.manager.state.recordDismiss(this.config.id);
      /* In the editor, closing the preview always reveals the teaser so its
         position can be set; otherwise that depends on the setting. */
      if (this.manager.designMode || this.config.teaserAfterDismiss) this.showTeaser();
    }

    this.emit('closed', { outcome });
    this.manager.release(this);
  }

  finish() {
    this.close('converted');
  }

  showTeaser() {
    if (this.teaser) this.teaser.hidden = false;
  }

  hideTeaser() {
    if (this.teaser) this.teaser.hidden = true;
  }

  emit(name, detail) {
    const payload = Object.assign({ campaign: this.config.id, name: this.config.name }, detail);
    this.dispatchEvent(new CustomEvent(`popup:${name}`, { bubbles: true, detail: payload }));

    /* Customer events is the only channel a custom pixel can hear from the
       page. Previews stay out of it, so checking a campaign in the editor or
       through ?popup= is never reported as a view. */
    const analytics = window.Shopify && window.Shopify.analytics;
    if (this.manager.previewing || !analytics || typeof analytics.publish !== 'function') return;
    try {
      analytics.publish(`oscc_popup:${name}`, payload);
    } catch (error) {
      console.warn('[popup] analytics publish failed', error);
    }
  }

  /* ------------------------------------------------------------------- steps */

  goToStep(index) {
    const steps = this.querySelectorAll('[data-popup-step]');
    if (index >= steps.length) {
      this.finish();
      return;
    }
    this.step = index;
    steps.forEach((step, position) => {
      step.hidden = position !== index;
    });
    const focusTarget = steps[index].querySelector('input, button');
    if (focusTarget) focusTarget.focus();
  }

  /* -------------------------------------------------------------- submission */

  async onSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    const form = event.currentTarget;
    const emailField = form.querySelector('[data-popup-email]');
    const phoneField = form.querySelector('[data-popup-phone]');
    const consentField = form.querySelector('[data-popup-consent]');

    this.clearError(form);

    if (emailField && !this.isValidEmail(emailField.value)) {
      this.showError(form, this.config.text.emailError);
      emailField.focus();
      return;
    }
    if (emailField && this.config.blockDisposable && this.isDisposableEmail(emailField.value)) {
      this.showError(form, this.config.text.disposableError);
      emailField.focus();
      return;
    }
    if (phoneField && (phoneField.required || phoneField.value) && !this.isValidPhone(phoneField.value)) {
      this.showError(form, this.config.text.phoneError);
      phoneField.focus();
      return;
    }
    if (consentField && consentField.required && !consentField.checked) {
      this.showError(form, this.config.text.consentError);
      return;
    }

    this.isSubmitting = true;
    this.setBusy(form, true);

    const email = emailField ? emailField.value.trim() : this.capturedEmail || '';
    const phone = phoneField ? phoneField.value.trim() : '';

    /* The theme editor preview rejects storefront form posts, so a live
       submission there always fails. It should not be attempted anyway: a
       merchant checking the design would be creating real subscribers every
       time they tested. Validation and every step after this still run, so
       the success and spin states preview exactly as a visitor sees them. */
    const preview = this.manager.designMode;

    try {
      if (emailField) {
        if (!preview) await this.submitToShopify(email);
        this.capturedEmail = email;
      }
      if (!preview && this.config.endpoint && (phone || emailField)) {
        await this.submitToEndpoint({
          email,
          phone,
          smsConsent: consentField ? consentField.checked : false,
          campaign: this.config.id,
          campaignName: this.config.name,
          tags: this.config.tags,
          pageUrl: window.location.href,
          consentText: this.consentText,
        });
      }
    } catch (error) {
      this.setBusy(form, false);
      this.isSubmitting = false;
      this.showError(form, error.message || this.config.text.genericError);
      return;
    }

    this.setBusy(form, false);
    this.isSubmitting = false;
    if (!this.manager.designMode) this.manager.state.recordConversion(this.config.id);
    this.emit('converted', { hasPhone: Boolean(phone) });

    if (this.canSpin && !this.hasSpun) {
      this.spin();
      return;
    }

    const codeButton = this.querySelector('[data-popup-copy]');
    if (this.config.autoApply && codeButton && codeButton.dataset.popupCopy) {
      this.applyDiscount(codeButton.dataset.popupCopy);
    }
    this.goToStep(this.step + 1);
  }

  /* TCPA record-keeping: the endpoint should store the exact wording the
     visitor agreed to, not just a boolean. */
  get consentText() {
    const node = this.querySelector('[data-popup-consent]');
    if (!node) return '';
    const label = node.closest('.popup__consent');
    return label ? label.textContent.trim() : '';
  }

  get signupFields() {
    const fields = { form_type: 'customer', utf8: '✓', 'contact[email]': this.pendingEmail };
    if (this.config.tags) fields['contact[tags]'] = this.config.tags;
    return fields;
  }

  /* Shopify protects customer and contact forms with hCaptcha, and it only
     wires itself up to a real form whose action contains /contact and which
     carries a form_type input. A background fetch has neither, so it arrives
     with no h-captcha-response token and Shopify answers with its challenge
     page instead of the form. That is what a visitor saw as "Something went
     wrong", and no amount of reading the response body could fix it.

     So the signup goes through a real form, handed to Shopify's own captcha
     before submitting, targeted at a hidden iframe so the visitor stays on
     the page. Same origin, so where the iframe lands is readable, and the
     redirect to ?customer_posted=true is still the signal for success.

     Docs: shopify.dev/docs/storefronts/themes/trust-security/captcha */
  submitToShopify(email) {
    this.pendingEmail = email;

    return new Promise((resolve, reject) => {
      const name = `popup-signup-${Date.now()}`;
      const frame = document.createElement('iframe');
      frame.name = name;
      frame.setAttribute('aria-hidden', 'true');
      frame.style.display = 'none';

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${window.Shopify.routes.root}contact`;
      form.target = name;
      form.acceptCharset = 'UTF-8';
      form.setAttribute('data-shopify-captcha', 'true');
      form.style.display = 'none';

      Object.entries(this.signupFields).forEach(([field, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field;
        input.value = value;
        form.appendChild(input);
      });

      const finish = (error) => {
        window.clearTimeout(timer);
        frame.remove();
        form.remove();
        if (error) reject(error);
        else resolve();
      };

      const timer = window.setTimeout(
        () => finish(new Error(this.config.text.genericError)),
        POPUP_SIGNUP_TIMEOUT
      );

      frame.addEventListener('load', () => {
        let landing;
        try {
          landing = this.readFrame(frame);
        } catch (error) {
          /* Shopify redirected off-origin, so there is nothing left to read
             and nothing to report as a failure. */
          finish();
          return;
        }

        /* Appending the iframe fires a load for its own blank document,
           which is not the response. */
        if (!landing.href || landing.href === 'about:blank') return;

        if (landing.href.includes('customer_posted=true')) {
          finish();
          return;
        }

        /* Landing here means the token was missing or rejected. It is not
           something the visitor can act on, so it is logged rather than
           explained to them. */
        if (landing.href.includes('/challenge')) {
          console.warn('[popup] signup was sent to the captcha challenge page', landing.href);
          finish(new Error(this.config.text.genericError));
          return;
        }

        /* The theme labels the footer newsletter's "thanks for subscribing"
           with the same .form__message class it uses for errors, separated
           only by a --success modifier, so the bare class cannot be read as
           a failure. ul.errors is what default_errors emits. */
        const error =
          landing.doc && landing.doc.querySelector('.errors, .form__message:not([class*="success"])');
        finish(new Error((error && error.textContent.trim()) || this.config.text.genericError));
      });

      document.body.appendChild(frame);
      document.body.appendChild(form);

      /* hCaptcha wires up on interaction with a form that was in the document
         at load. This one is built and submitted programmatically, so it has
         to be handed over explicitly or it submits without a token. When
         spam protection is switched off in the admin, captcha is absent and
         the form just goes. */
      const captcha = window.Shopify && window.Shopify.captcha;
      if (captcha && typeof captcha.protect === 'function') captcha.protect(form, () => form.submit());
      else form.submit();
    });
  }

  /* Split out because a test environment cannot navigate a real iframe. */
  readFrame(frame) {
    return { href: frame.contentWindow.location.href, doc: frame.contentDocument };
  }

  async submitToEndpoint(payload) {
    const response = await fetch(this.config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(this.config.text.genericError);
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
  }

  isDisposableEmail(value) {
    const domain = value.trim().toLowerCase().split('@')[1];
    if (!domain) return false;
    return DISPOSABLE_CORE.includes(domain) || this.manager.disposableDomains.has(domain);
  }

  isValidPhone(value) {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }

  setBusy(form, busy) {
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    button.classList.toggle('popup__button--loading', busy);
    button.disabled = busy;
  }

  /* Falls back to the generic message so a blanked-out setting in the theme
     editor can never surface an empty error. */
  showError(form, message) {
    const target = form.querySelector('[data-popup-error]');
    if (!target) return;
    target.textContent = message || this.config.text.genericError || 'Something went wrong. Please try again.';
    target.hidden = false;
  }

  clearError(form) {
    const target = form.querySelector('[data-popup-error]');
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }

  async copyCode(button) {
    const code = button.dataset.popupCopy;
    try {
      await navigator.clipboard.writeText(code);
      button.classList.add('popup__code--copied');
      window.setTimeout(() => button.classList.remove('popup__code--copied'), 2000);
    } catch (error) {
      /* Clipboard access denied: the code is already visible as text. */
    }
  }

  /* ------------------------------------------------------------------- wheel */

  /* The wheel is drawn here rather than in Liquid because slice geometry needs
     trigonometry, which Liquid has no filters for. Segment 1 starts at the
     pointer (12 o'clock) and they run clockwise. */
  setupWheel() {
    this.slices = JSON.parse(this.wheel.dataset.slices);
    this.wheelFace = this.wheel.querySelector('[data-popup-wheel-face]');
    if (!this.slices.length) return;

    const svgNS = 'http://www.w3.org/2000/svg';
    const data = this.wheel.dataset;
    /* Duplicates and blanks are dropped so a merchant who leaves the third
       colour at its default does not get a palette that repeats. */
    const palette = [data.colorA, data.colorB, data.colorC].filter(
      (color, index, all) => color && all.indexOf(color) === index
    );
    if (!palette.length) palette.push('#17263d');

    /* The outline is a colour of its own rather than the card colour, so
       wedges stay separated even when a segment colour and the popup
       background happen to match. */
    const outline = data.outlineColor || '#000000';
    const rimColor = data.rimColor || palette[0];
    const count = this.slices.length;
    const colors = assignSliceColors(count, palette);
    const sliceAngle = 360 / count;
    const center = 100;
    const radius = 94;

    /* The inner rim sits directly inside the outer one rather than at some
       fraction of the radius, so the two read as a single double border
       however the widths are tuned. */
    const outerRimWidth = 5;
    const innerRimWidth = 2.5;
    const innerRimRadius = radius - outerRimWidth / 2 - innerRimWidth / 2;

    /* Labels live in the band between the hub and the inner rim, centred in
       it, so they never run out over either ring. */
    const hubRadius = radius * 0.2;
    const labelRadius = (hubRadius + innerRimRadius) / 2;
    const labelSpan = innerRimRadius - hubRadius - 6;

    const pointAt = (degrees) => {
      const radians = ((degrees - 90) * Math.PI) / 180;
      return [center + radius * Math.cos(radians), center + radius * Math.sin(radians)];
    };

    this.slices.forEach((slice, index) => {
      const start = index * sliceAngle;
      const end = start + sliceAngle;
      const [x1, y1] = pointAt(start);
      const [x2, y2] = pointAt(end);
      const largeArc = sliceAngle > 180 ? 1 : 0;
      const fill = colors[index];

      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute(
        'd',
        count === 1
          ? `M ${center} ${center} m -${radius} 0 a ${radius} ${radius} 0 1 0 ${radius * 2} 0 a ${radius} ${radius} 0 1 0 -${radius * 2} 0`
          : `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
      );
      path.setAttribute('fill', fill);
      path.setAttribute('stroke', outline);
      path.setAttribute('stroke-width', '1.5');
      path.setAttribute('stroke-linejoin', 'round');
      this.wheelFace.appendChild(path);

      const text = document.createElementNS(svgNS, 'text');
      const midAngle = start + sliceAngle / 2;
      const [label, fontSize] = fitLabel(slice.label, labelSpan, count > 6 ? 8 : 10);

      /* Labels run radially outward. Angles are measured clockwise from
         twelve, so a label is upright through the right half of the wheel and
         inverted through the left. Turning those a further 180 degrees and
         mirroring the anchor puts them back the right way up in place. */
      const upsideDown = midAngle >= 180;
      const offset = upsideDown ? -labelRadius : labelRadius;
      const angle = upsideDown ? midAngle + 90 : midAngle - 90;

      text.setAttribute('x', center + offset);
      text.setAttribute('y', center);
      text.setAttribute('fill', readableInk(fill));
      text.setAttribute('font-size', String(fontSize));
      text.setAttribute('font-weight', '700');
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('transform', `rotate(${angle} ${center} ${center})`);
      text.textContent = label;
      this.wheelFace.appendChild(text);
    });

    /* Rims give the wheel edges of its own, so it still reads as a wheel when
       a segment colour matches whatever sits behind it. The inner ring picks
       up the segment outline rather than the accent, closing off the wedges
       so they read as properly bordered shapes. */
    const addRing = (ringRadius, width, stroke) => {
      const ring = document.createElementNS(svgNS, 'circle');
      ring.setAttribute('cx', center);
      ring.setAttribute('cy', center);
      ring.setAttribute('r', ringRadius);
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', stroke);
      ring.setAttribute('stroke-width', width);
      this.wheelFace.appendChild(ring);
    };

    addRing(radius, outerRimWidth, rimColor);
    addRing(innerRimRadius, innerRimWidth, outline);

    this.drawWheelFurniture({ svgNS, center, rimColor, outline, hubColor: data.hubColor });
  }

  /* The pointer and hub sit in a layer of their own because the face spins
     and they must not. Both are outlined so they stay visible when the wedge
     behind them happens to be the same colour. */
  drawWheelFurniture({ svgNS, center, rimColor, outline, hubColor }) {
    const overlay = this.wheel.querySelector('[data-popup-wheel-overlay]');
    if (!overlay) return;
    overlay.textContent = '';

    const pointer = document.createElementNS(svgNS, 'path');
    pointer.setAttribute('d', `M ${center - 13} 0 L ${center + 13} 0 L ${center} 29 Z`);
    pointer.setAttribute('fill', rimColor);
    pointer.setAttribute('stroke', outline);
    pointer.setAttribute('stroke-width', '2.5');
    pointer.setAttribute('stroke-linejoin', 'round');
    overlay.appendChild(pointer);

    const hub = document.createElementNS(svgNS, 'circle');
    hub.setAttribute('cx', center);
    hub.setAttribute('cy', center);
    hub.setAttribute('r', '15');
    hub.setAttribute('fill', hubColor || '#ffffff');
    hub.setAttribute('stroke', outline);
    hub.setAttribute('stroke-width', '2.5');
    overlay.appendChild(hub);

    const hubRing = document.createElementNS(svgNS, 'circle');
    hubRing.setAttribute('cx', center);
    hubRing.setAttribute('cy', center);
    hubRing.setAttribute('r', '10');
    hubRing.setAttribute('fill', 'none');
    hubRing.setAttribute('stroke', rimColor);
    hubRing.setAttribute('stroke-width', '2.5');
    overlay.appendChild(hubRing);
  }

  /* Every segment label can be blanked in the editor, which leaves a wheel
     with nothing to land on. Treat that as an ordinary popup rather than
     spinning into an undefined prize. */
  get canSpin() {
    return Boolean(this.wheel) && Array.isArray(this.slices) && this.slices.length > 0;
  }

  spin() {
    if (this.hasSpun || !this.canSpin) return;
    this.hasSpun = true;

    const index = this.pickSlice();
    const slice = this.slices[index];
    const sliceAngle = 360 / this.slices.length;
    const turns = 6;
    const rotation = 360 * turns - (index * sliceAngle + sliceAngle / 2);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 0 : 4800;

    this.wheelFace.style.transition = `transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1)`;
    this.wheelFace.style.transform = `rotate(${rotation}deg)`;

    window.setTimeout(() => this.revealPrize(slice), duration + 250);
  }

  /* Weighted draw. A weight of 0 makes a slice decorative: it is visible on
     the wheel but can never be landed on. */
  pickSlice() {
    const weights = this.slices.map((slice) => Math.max(0, slice.weight || 0));
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total <= 0) return Math.floor(Math.random() * this.slices.length);

    let draw = Math.random() * total;
    for (let index = 0; index < weights.length; index += 1) {
      draw -= weights[index];
      if (draw <= 0) return index;
    }
    return weights.length - 1;
  }

  revealPrize(slice) {
    const successStep = this.querySelector('[data-popup-step="success"]');
    if (successStep) {
      const label = successStep.querySelector('[data-popup-prize-label]');
      const codeButton = successStep.querySelector('[data-popup-copy]');
      if (label) label.textContent = slice.label;

      /* A losing segment still gets a success screen, just without a code. */
      if (codeButton) {
        codeButton.hidden = !slice.code;
        if (slice.code) {
          codeButton.dataset.popupCopy = slice.code;
          const codeText = codeButton.querySelector('[data-popup-code-text]');
          if (codeText) codeText.textContent = slice.code;
        }
      }
      if (slice.code && this.config.autoApply) this.applyDiscount(slice.code);
    }
    this.emit('prize', { prize: slice.label, code: slice.code });
    this.goToStep(this.step + 1);
  }

  applyDiscount(code) {
    fetch(`${window.Shopify.routes.root}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      credentials: 'same-origin',
    }).catch(() => {});
  }
}

/* Orders eligible campaigns best-first. Targeting beats configuration: a
   campaign pointed at this page wins over a sitewide one regardless of the
   priority slider, which only settles ties between equally targeted
   campaigns. Equal on both, the earlier block in the editor wins, which falls
   out of Array.prototype.sort being stable. */
function rankCampaigns(a, b) {
  const specificity = (b.config.specificity || 1) - (a.config.specificity || 1);
  if (specificity !== 0) return specificity;
  return (b.config.priority || 0) - (a.config.priority || 0);
}

class PopupCampaigns extends HTMLElement {
  connectedCallback() {
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.state = new PopupState();
    this.state.markFirstSeen();
    this.active = null;
    this.disposableDomains = new Set();

    this.campaigns = Array.from(this.querySelectorAll('popup-campaign'));
    this.campaigns.forEach((campaign) => {
      campaign.manager = this;
    });

    this.bindTriggerLinks();

    if (this.designMode) {
      this.bindDesignMode();
      return;
    }

    /* Checked before the election so that a campaign which would lose it, or
       which this visitor has already been shown, can still be opened. */
    const forced = this.forcedCampaign();
    if (forced) {
      this.forcing = true;
      forced.open('url');
      return;
    }

    /* Exactly one campaign is allowed to run on a page. Arming every eligible
       campaign and letting the claim lock sort it out would hand the page to
       whichever trigger happened to fire first, so the winner is elected up
       front and the runners-up are left dormant. */
    const winner = this.campaigns.filter((campaign) => this.isEligible(campaign)).sort(rankCampaigns)[0];

    if (winner) {
      if (winner.config.teaserAlways) winner.showTeaser();
      winner.arm();
    }
  }

  /* Loaded on first open rather than at page load: the full list is 50KB
     gzipped and only matters once someone is looking at a form. A visitor
     takes seconds to type an address, so it is normally in place well before
     it is needed, and if it is not, the inline core list still applies and
     the app proxy rejects the rest server-side anyway. */
  loadDisposableDomains() {
    if (this.disposableRequested || !this.dataset.disposableSrc) return;
    this.disposableRequested = true;

    const script = document.createElement('script');
    script.src = this.dataset.disposableSrc;
    script.async = true;
    script.addEventListener('load', () => {
      if (Array.isArray(window.POPUP_DISPOSABLE_DOMAINS)) {
        this.disposableDomains = new Set(window.POPUP_DISPOSABLE_DOMAINS);
      }
    });
    document.head.appendChild(script);
  }

  /* The editor preview and a ?popup= link both put a campaign on screen
     outside the normal rules, so neither should spend the visitor's
     frequency budget. Otherwise checking a once-ever campaign is the one
     time it ever runs. */
  get previewing() {
    return this.designMode || this.forcing === true;
  }

  /* ?popup=<name|block id> opens that campaign on load, past targeting,
     triggers and frequency alike; ?popup=any takes whichever campaign would
     otherwise win the page. Matching on the campaign name is what makes the
     link shareable, since block ids are not visible anywhere a merchant
     looks. A campaign only renders on the pages it targets, so the link has
     to point at one of them — when nothing matches, what is available here
     is listed in the console rather than failing silently. */
  forcedCampaign() {
    const requested = (new URLSearchParams(window.location.search).get(POPUP_FORCE_PARAM) || '').trim();
    if (!requested) return null;

    const live = this.campaigns.filter((campaign) => campaign.config.enabled !== false);
    if (requested === 'any' || requested === '1') return [...live].sort(rankCampaigns)[0] || null;

    const wanted = requested.toLowerCase();
    const match = this.campaigns.find(
      (campaign) =>
        campaign.config.id === requested ||
        String(campaign.config.name || '')
          .trim()
          .toLowerCase() === wanted
    );

    if (!match) {
      console.warn(
        `[popup] nothing on this page matches ?${POPUP_FORCE_PARAM}=${requested}. Campaigns available here:`,
        this.campaigns.map((campaign) => ({ name: campaign.config.name, id: campaign.config.id }))
      );
    }

    return match || null;
  }

  bindTriggerLinks() {
    document.querySelectorAll('[data-popup-open]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const target = this.campaigns.find((campaign) => campaign.config.id === link.dataset.popupOpen);
        if (target) target.open('manual');
      });
    });
  }

  bindDesignMode() {
    /* In the editor, selecting a campaign block previews it immediately and
       frequency rules are ignored. */
    document.addEventListener('shopify:block:select', (event) => {
      const campaign = this.campaigns.find((item) => item.config.id === event.detail.blockId);
      if (!campaign) return;
      if (this.active && this.active !== campaign) {
        this.active.close('preview');
        this.active.hideTeaser();
      }
      campaign.open('preview');
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const campaign = this.campaigns.find((item) => item.config.id === event.detail.blockId);
      if (!campaign) return;
      campaign.close('preview');
      campaign.hideTeaser();
    });
  }

  claim(campaign) {
    if (this.active) return false;
    this.active = campaign;
    return true;
  }

  release(campaign) {
    if (this.active === campaign) this.active = null;
  }

  isEligible(campaign) {
    const config = campaign.config;
    const record = this.state.campaign(config.id);
    const now = Date.now();

    if (config.enabled === false) return false;
    if (this.state.sessionShows >= this.maxPerSession) return false;
    if (this.state.pageviews < (config.minPageviews || 1)) return false;
    if (config.customerVisibility === 'hide_logged_in' && this.hasCustomer) return false;
    if (config.customerVisibility === 'hide_purchasers' && this.hasOrders) return false;
    if (config.hideForSubscribers && this.state.hasSubscribed) return false;

    if (config.audience === 'new' && this.state.isReturningVisitor) return false;
    if (config.audience === 'returning' && !this.state.isReturningVisitor) return false;

    if (!this.matchesDevice(config.devices)) return false;
    if (!this.withinSchedule(config)) return false;
    const share = typeof config.trafficPercent === 'number' ? config.trafficPercent : 100;
    if (this.state.bucket >= share) return false;

    if (record.convertedAt) {
      if (config.successSuppressDays === 0) return false;
      if (now - record.convertedAt < config.successSuppressDays * POPUP_DAY) return false;
    }

    if (config.frequency === 'once_ever' && record.shows) return false;
    if (config.frequency === 'session' && this.state.sessionCampaign(config.id).shows) return false;
    if (config.frequency === 'days' && record.lastShown) {
      if (now - record.lastShown < config.frequencyDays * POPUP_DAY) return false;
    }

    if (record.dismissedAt && config.redisplayDays) {
      if (now - record.dismissedAt < config.redisplayDays * POPUP_DAY) return false;
    }

    return true;
  }

  matchesDevice(devices) {
    if (!devices || devices === 'all') return true;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    return devices === 'mobile' ? isMobile : !isMobile;
  }

  withinSchedule(config) {
    const now = Date.now();
    if (config.startDate) {
      const start = Date.parse(config.startDate);
      if (!Number.isNaN(start) && now < start) return false;
    }
    if (config.endDate) {
      const end = Date.parse(config.endDate);
      if (!Number.isNaN(end) && now > end) return false;
    }
    return true;
  }

  get hasCustomer() {
    return this.dataset.customer === 'true';
  }

  get hasOrders() {
    return this.dataset.hasOrders === 'true';
  }

  get maxPerSession() {
    return Number(this.dataset.maxPerSession) || 1;
  }
}

customElements.define('popup-campaign', PopupCampaign);
customElements.define('popup-campaigns', PopupCampaigns);
